/**
 * Coups de pouce — Attendu 681 « Fréquence observée vs probabilité ».
 *
 * Clé persistée au niveau EXERCICE : une entrée par exercice, servie à tous
 * ses formats.
 *
 * Règle absolue : l'aide porte sur d'AUTRES valeurs que celles que le
 * générateur peut tirer (25 lancers : absent des pools ; roue à 8 secteurs :
 * aucune expérience du pool ; cible 0,42 : absente des 27 cibles graphique).
 * Emphase : **texte** (pas de HTML brut). Pas de tiret cadratin.
 */

// ── Fréquence : succès sur lancers, proche de la probabilité (id cp681play1) ──
const SVG_FREQUENCE_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 216" role="img"
     aria-label="Léa lance une pièce 25 fois et obtient 11 fois Pile. La fréquence est 11 sur 25, soit 0,44. La probabilité théorique est 0,5 : la fréquence est proche, mais pas égale">
  <g id="cp681play1" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" font-family="system-ui,sans-serif">25 lancers d'une pièce, 11 fois Pile</text>
  <!-- la fraction s'assemble -->
  <g opacity="0">
    <text x="30" y="70" font-size="14" fill="#0f172a" font-family="system-ui,sans-serif">Fréquence =</text>
    <text x="135" y="62" font-size="15" font-weight="700" fill="#378ADD" text-anchor="middle" font-family="system-ui,sans-serif">11</text>
    <line x1="118" y1="66" x2="152" y2="66" stroke="#0f172a" stroke-width="1.5"/>
    <text x="135" y="82" font-size="15" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">25</text>
    <text x="163" y="70" font-size="12" fill="#64748b" font-family="system-ui,sans-serif">(succès sur lancers)</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play1.click+0.4s" fill="freeze"/>
  </g>
  <g opacity="0">
    <text x="30" y="112" font-size="14" font-weight="700" fill="#378ADD" font-family="system-ui,sans-serif">11/25 = 0,44</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play1.click+1.4s" fill="freeze"/>
  </g>
  <!-- la probabilité théorique à côté -->
  <g opacity="0">
    <text x="200" y="112" font-size="14" font-weight="700" fill="#1D9E75" font-family="system-ui,sans-serif">Probabilité = 0,5</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play1.click+2.2s" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="90" y="130" width="180" height="26" rx="13" fill="none" stroke="#F97316" stroke-width="1.5"/>
    <text x="180" y="147" font-size="13" font-weight="700" fill="#F97316" text-anchor="middle" font-family="system-ui,sans-serif">proche, mais pas égale</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play1.click+3s" fill="freeze"/>
  </g>
  <!-- conclusions -->
  <g opacity="0">
    <rect x="20" y="172" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="36" y="180" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Fréquence = nombre de succès sur nombre de lancers.</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play1.click+3.9s" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="20" y="192" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="36" y="200" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Elle est proche de la probabilité, sans être égale.</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play1.click+4.5s" fill="freeze"/>
  </g>
</svg>`.trim();

const CP_FREQUENCE = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                'La **fréquence observée**, c\'est le nombre de **succès** divisé par le nombre de **lancers**.',
            ],
            svg: SVG_FREQUENCE_ANIM,
        },
    ],
};

// ── Estimer : la fraction d'un nombre, 3/8 × 80 (id cp681play2) ──
// Refonte finale (décisions utilisateur 2026-08-06) : l'espérance N × p se
// calcule comme une FRACTION D'UN NOMBRE, technique déjà connue en 6ème.
// Des cases contenant chacune la fraction 3/8, avec des pointillés pour
// signifier la répétition en 80 fois (accolade « 80 fois » dessous), puis
// la multiplication 3/8 × 80 = 30. Exemple à 3 dorés (pas 1) : ~40 % des
// tirages du pool ont plusieurs cas favorables. Roue à 8 secteurs absente
// du pool.
const SVG_ESTIMER_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 232" role="img"
     aria-label="Une roue à 8 secteurs dont 3 dorés, tournée 80 fois. Des cases contiennent chacune la fraction 3 sur 8, avec des pointillés pour montrer que cela se répète 80 fois. En dessous, la multiplication 3 sur 8 fois 80 égale 30 : environ 30 dorés attendus">
  <g id="cp681play2" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <text x="8" y="44" font-size="13" font-weight="600" fill="#64748b" font-family="system-ui,sans-serif">Roue à 8 secteurs dont 3 dorés, tournée 80 fois</text>
  <!-- les cases 3/8, une par tour, pointillés pour la répétition en 80 fois -->
  <g opacity="0"><rect x="40" y="70" width="36" height="36" rx="4" fill="#e2e8f0" stroke="#1E3A8A" stroke-width="1.5"/><text x="58" y="86" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">3</text><line x1="50" y1="90" x2="66" y2="90" stroke="#0f172a" stroke-width="1.5"/><text x="58" y="103" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">8</text><animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.2s" begin="cp681play2.click+0.3s" fill="freeze"/></g>
  <g opacity="0"><rect x="80" y="70" width="36" height="36" rx="4" fill="#e2e8f0" stroke="#1E3A8A" stroke-width="1.5"/><text x="98" y="86" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">3</text><line x1="90" y1="90" x2="106" y2="90" stroke="#0f172a" stroke-width="1.5"/><text x="98" y="103" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">8</text><animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.2s" begin="cp681play2.click+0.5s" fill="freeze"/></g>
  <g opacity="0"><rect x="120" y="70" width="36" height="36" rx="4" fill="#e2e8f0" stroke="#1E3A8A" stroke-width="1.5"/><text x="138" y="86" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">3</text><line x1="130" y1="90" x2="146" y2="90" stroke="#0f172a" stroke-width="1.5"/><text x="138" y="103" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">8</text><animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.2s" begin="cp681play2.click+0.7s" fill="freeze"/></g>
  <g opacity="0"><rect x="160" y="70" width="36" height="36" rx="4" fill="#e2e8f0" stroke="#1E3A8A" stroke-width="1.5"/><text x="178" y="86" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">3</text><line x1="170" y1="90" x2="186" y2="90" stroke="#0f172a" stroke-width="1.5"/><text x="178" y="103" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">8</text><animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.2s" begin="cp681play2.click+0.9s" fill="freeze"/></g>
  <g opacity="0"><rect x="200" y="70" width="36" height="36" rx="4" fill="#e2e8f0" stroke="#1E3A8A" stroke-width="1.5"/><text x="218" y="86" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">3</text><line x1="210" y1="90" x2="226" y2="90" stroke="#0f172a" stroke-width="1.5"/><text x="218" y="103" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">8</text><animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.2s" begin="cp681play2.click+1.1s" fill="freeze"/></g>
  <!-- pointillés : ça continue jusqu'à 80 fois -->
  <text x="252" y="94" font-size="18" font-weight="700" fill="#64748b" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">…<animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.2s" begin="cp681play2.click+1.3s" fill="freeze"/></text>
  <!-- dernière case -->
  <g opacity="0"><rect x="270" y="70" width="36" height="36" rx="4" fill="#e2e8f0" stroke="#1E3A8A" stroke-width="1.5"/><text x="288" y="86" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">3</text><line x1="280" y1="90" x2="296" y2="90" stroke="#0f172a" stroke-width="1.5"/><text x="288" y="103" font-size="12" font-weight="700" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">8</text><animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.2s" begin="cp681play2.click+1.5s" fill="freeze"/></g>
  <!-- la grande accolade : 80 fois -->
  <g opacity="0">
    <path d="M40 112 Q40 120 51 120 L160 120 Q173 120 173 128 Q173 120 186 120 L295 120 Q306 120 306 112" fill="none" stroke="#1E3A8A" stroke-width="1.5"/>
    <text x="173" y="146" font-size="14" font-weight="700" fill="#1E3A8A" text-anchor="middle" font-family="system-ui,sans-serif">80 fois</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.35s" begin="cp681play2.click+2s" fill="freeze"/>
  </g>
  <!-- la multiplication : 3/8 × 80, puis le résultat -->
  <g opacity="0">
    <text x="130" y="165" font-size="13" font-weight="700" fill="#B45309" text-anchor="middle" font-family="system-ui,sans-serif">3</text>
    <line x1="121" y1="169" x2="139" y2="169" stroke="#B45309" stroke-width="1.5"/>
    <text x="130" y="182" font-size="13" font-weight="700" fill="#B45309" text-anchor="middle" font-family="system-ui,sans-serif">8</text>
    <text x="147" y="174" font-size="14" font-weight="700" fill="#B45309" font-family="system-ui,sans-serif">× 80</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.35s" begin="cp681play2.click+2.8s" fill="freeze"/>
  </g>
  <g opacity="0">
    <text x="185" y="174" font-size="14" font-weight="700" fill="#B45309" font-family="system-ui,sans-serif">= 30</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.35s" begin="cp681play2.click+3.6s" fill="freeze"/>
  </g>
  <!-- bilan -->
  <text x="280" y="174" font-size="14" font-weight="700" fill="#0f172a" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">environ 30 dorés<animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play2.click+4.4s" fill="freeze"/></text>
  <!-- conclusions -->
  <g opacity="0">
    <rect x="20" y="196" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="36" y="204" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">La fraction 3/8 se répète 80 fois.</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play2.click+5.2s" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="20" y="216" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="36" y="224" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Environ 30 dorés, pas exactement 30.</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play2.click+5.8s" fill="freeze"/>
  </g>
</svg>`.trim();

const CP_ESTIMER = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                'À chaque tour, il y a **3 chances sur 8** d\'obtenir un doré. Sur 80 tours, on peut donc s\'attendre à environ **3/8 de 80** dorés.',
            ],
            svg: SVG_ESTIMER_ANIM,
        },
    ],
};

// ── Graphique : lire où la courbe se calme (id cp681play3) ──
// Cible 0,42 : absente des 27 cibles du générateur. Courbe FIXE (pas de
// hasard) : zigzag marqué puis stabilisation sur le trait.
const SVG_GRAPH_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 216" role="img"
     aria-label="Un graphique de fréquence : la courbe zigzague au début, puis se calme le long d'un trait pointillé étiqueté 0,42. La valeur où la courbe se calme est la probabilité">
  <g id="cp681play3" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <!-- axes -->
  <line x1="40" y1="150" x2="330" y2="150" stroke="#0f172a" stroke-width="1.5"/>
  <line x1="40" y1="40" x2="40" y2="150" stroke="#0f172a" stroke-width="1.5"/>
  <text x="34" y="154" font-size="11" fill="#475569" text-anchor="end" font-family="system-ui,sans-serif">0</text>
  <text x="34" y="46" font-size="11" fill="#475569" text-anchor="end" font-family="system-ui,sans-serif">1</text>
  <text x="185" y="166" font-size="11" fill="#475569" text-anchor="middle" font-family="system-ui,sans-serif">Nombre de lancers</text>
  <!-- trait pointillé cible 0,42 → y = 150 − 0,42·110 ≈ 103.8 -->
  <line x1="40" y1="103.8" x2="330" y2="103.8" stroke="#94a3b8" stroke-width="1" stroke-dasharray="4 3"/>
  <text x="34" y="107" font-size="11" fill="#475569" text-anchor="end" font-family="system-ui,sans-serif">0,42</text>
  <!-- courbe fixe : zigzag puis calme, dessinée progressivement -->
  <polyline points="48,60 62,138 76,75 90,125 108,88 128,115 150,97 175,110 205,100 240,106 275,102 310,104"
            fill="none" stroke="#E24B4A" stroke-width="2" stroke-dasharray="600" stroke-dashoffset="600">
    <animate attributeName="stroke-dashoffset" values="600;0" keyTimes="0;1" dur="2.5s" begin="cp681play3.click+0.4s" fill="freeze"/>
  </polyline>
  <g opacity="0">
    <text x="88" y="52" font-size="12" font-weight="700" fill="#E24B4A" text-anchor="middle" font-family="system-ui,sans-serif">ça bouge…</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play3.click+1.2s" fill="freeze"/>
  </g>
  <g opacity="0">
    <text x="262" y="88" font-size="12" font-weight="700" fill="#1D9E75" text-anchor="middle" font-family="system-ui,sans-serif">…puis ça se calme ici</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play3.click+3.1s" fill="freeze"/>
  </g>
  <!-- conclusions -->
  <g opacity="0">
    <rect x="20" y="182" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="36" y="190" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Au début, la fréquence bouge beaucoup.</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play3.click+3.9s" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="20" y="202" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="36" y="210" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">La valeur où elle se calme est la probabilité.</text>
    <animate attributeName="opacity" values="0;1" keyTimes="0;1" dur="0.4s" begin="cp681play3.click+4.5s" fill="freeze"/>
  </g>
</svg>`.trim();

const CP_GRAPH = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                'Regarde où la courbe **arrête de zigzaguer** : elle se calme le long d\'un trait — c\'est la **probabilité**.',
            ],
            svg: SVG_GRAPH_ANIM,
        },
    ],
};

const CP_681 = {
    comparer_frequence_probabilite: CP_FREQUENCE,
    estimer_succes_grand_nombre: CP_ESTIMER,
    interpreter_graphique_stabilisation: CP_GRAPH,
};

export default CP_681;
